import { useEffect, useState } from 'react';
import { useMarketStore } from '../../store/marketStore';
import { useWatchlistStore } from '../../store/watchlistStore';
import { api } from '../../services/api';
import type { MarketType } from '../../types/market';
import { CoinIcon } from '../UI/CoinIcon';
import { CoinTagFlag } from './CoinTagFlag';
import { getExchange } from '../../utils/exchange';

const MARKETS: MarketType[] = ['crypto', 'forex', 'stocks'];
const SEARCH_DEBOUNCE_MS = 300;

function PlusIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5" fill="none">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5" fill="none">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

export function SymbolList() {
  const { activeSymbol, activeMarket, setActiveSymbol, setMarket } = useMarketStore();
  const { symbols: watchlist, addSymbol } = useWatchlistStore();
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<string[]>([]);
  const [searching, setSearching] = useState(false);

  const query = search.trim();
  const isSearching = query.length > 0;

  // Debounced live search against the backend — covers every symbol the
  // exchange offers, not just the ones already in the watchlist.
  useEffect(() => {
    if (!query || activeMarket !== 'crypto') {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const { results: found } = await api.searchSymbols(query, activeMarket);
        // Keep results consistent with the rest of the app, which only deals in USDT pairs.
        const filtered = found.filter((s) => s.endsWith('USDT'));
        if (!cancelled) setResults(filtered);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query, activeMarket]);

  const list = isSearching ? results : watchlist;

  return (
    <div className="flex flex-col h-full bg-[var(--bg-app)] border-r border-[var(--border-color)]">
      {/* Market tabs */}
      <div className="flex gap-1 p-2 border-b border-[var(--border-color)]">
        {MARKETS.map((m) => (
          <button
            key={m}
            onClick={() => setMarket(m)}
            className={`flex-1 py-1 rounded text-xs capitalize font-medium transition-colors ${
              activeMarket === m
                ? 'bg-blue-600 text-white'
                : 'text-[var(--text-muted)] hover:bg-[var(--bg-hover)]'
            }`}
          >
            {m}
          </button>
        ))}
      </div>

      {activeMarket === 'crypto' ? (
        <>
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="m-2 px-2 py-1 text-sm bg-[var(--bg-panel)] border border-[var(--border-color)] rounded text-[var(--text-secondary)] outline-none focus:border-blue-500 themed-placeholder"
          />

          <div className="flex-1 overflow-y-auto">
            {isSearching && searching && (
              <div className="px-3 py-2 text-xs text-[var(--text-muted)]">Searching…</div>
            )}
            {isSearching && !searching && list.length === 0 && (
              <div className="px-3 py-2 text-xs text-[var(--text-muted)]">No matches</div>
            )}
            {list.map((sym) => {
              const inWatchlist = watchlist.includes(sym);
              const exchange = getExchange(sym);
              return (
                <div
                  key={sym}
                  onClick={() => setActiveSymbol(sym)}
                  className={`group w-full flex items-center justify-between gap-2 px-3 py-2 text-sm font-mono transition-colors border-l-2 cursor-pointer ${
                    activeSymbol === sym
                      ? 'bg-blue-600/20 text-blue-300 border-blue-500'
                      : 'text-[var(--text-secondary)] hover:bg-[var(--bg-panel)] border-transparent'
                  }`}
                >
                  <span className="flex items-center gap-2">{!isSearching && <CoinTagFlag symbol={sym} />}<CoinIcon symbol={sym} size={18} /><span>{sym}</span>{exchange && <span className="text-[10px] font-sans text-[var(--text-muted)]">{exchange}</span>}</span>
                  {isSearching && (
                    <button
                      onClick={(e) => { e.stopPropagation(); if (!inWatchlist) addSymbol(sym); }}
                      disabled={inWatchlist}
                      title={inWatchlist ? 'Already in watchlist' : 'Add to watchlist'}
                      className={`w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0 ${
                        inWatchlist ? 'text-emerald-500' : 'text-[var(--text-muted)] hover:text-white hover:bg-blue-600'
                      }`}
                    >
                      {inWatchlist ? <CheckIcon /> : <PlusIcon />}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="text-[var(--text-muted)] text-sm capitalize font-medium mb-1">{activeMarket}</div>
            <div className="text-[var(--text-muted)] text-xs">Coming Soon</div>
          </div>
        </div>
      )}
    </div>
  );
}
